'use client'

import {
  type PersistedClient,
  type Persister,
} from '@tanstack/react-query-persist-client'
import type { Query } from '@tanstack/react-query'
import { del, get, keys, set } from 'idb-keyval'

import { getBugsnag } from '@/utils/bugsnag.client'

export const QUERY_PERSIST_PREFIX = 'rq-skop-manage:'
export const LEGACY_QUERY_PERSIST_KEY = 'rq-skop-manage-v1'
export const QUERY_PERSIST_BUSTER = 'lookup-deletion-witness-v2'
export const QUERY_PERSIST_MAX_AGE = 24 * 60 * 60 * 1000
export const QUERY_PERSIST_DEBOUNCE_MS = 250

type PersistSkipReason = 'not-opted-in' | 'not-cloneable' | 'infinite' | 'error'

type PersistDecision = {
  persist: boolean
  reason?: PersistSkipReason
}

const loggedPersistenceIssues = new Set<string>()
// Query data is updated immutably. Cache only the last payload's cloneability,
// without keeping removed queries (and their data) alive across cache scopes.
const queryCloneDecisions = new WeakMap<
  Query,
  { data: unknown; decision: PersistDecision }
>()

function isInfiniteQueryData(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false

  const candidate = value as { pages?: unknown; pageParams?: unknown }
  return Array.isArray(candidate.pages) && Array.isArray(candidate.pageParams)
}

function isStructuredCloneCompatible(value: unknown): boolean {
  const structuredCloneFn = globalThis.structuredClone

  if (typeof structuredCloneFn === 'function') {
    structuredCloneFn(value)
    return true
  }

  JSON.stringify(value)
  return true
}

function isDataCloneLikeError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === 'DataCloneError' ||
      error.message.includes('could not be cloned'))
  )
}

function reportPersistenceIssue(params: {
  reason: PersistSkipReason
  query?: Query
  error?: unknown
}): void {
  const { reason, query, error } = params
  const dedupeKey = `${reason}:${query?.queryHash ?? 'snapshot'}`

  if (loggedPersistenceIssues.has(dedupeKey)) {
    return
  }
  loggedPersistenceIssues.add(dedupeKey)

  const details = {
    reason,
    queryHash: query?.queryHash,
    queryKey: query?.queryKey,
    error:
      error instanceof Error
        ? {
            name: error.name,
            message: error.message,
          }
        : error,
  }

  if (process.env.NODE_ENV !== 'production') {
    console.warn('[react-query-persistence]', details)
  }

  if (reason === 'not-opted-in' || reason === 'infinite') {
    return
  }

  try {
    const bugsnag = getBugsnag()
    const issue =
      error instanceof Error
        ? error
        : new Error(`React Query persistence skipped: ${reason}`)

    bugsnag?.notify(issue, (event) => {
      event.addMetadata('reactQueryPersistence', details)
    })
  } catch {}
}

export function shouldPersistLookupQuery(query: Query): boolean {
  return getLookupQueryPersistDecision(query).persist
}

export function getLookupQueryPersistDecision(query: Query): PersistDecision {
  try {
    if (
      query.state.status !== 'success' ||
      query.state.data === undefined ||
      query.state.fetchStatus !== 'idle'
    ) {
      return { persist: false }
    }

    if (query.meta?.persistence !== 'lookup') {
      reportPersistenceIssue({ reason: 'not-opted-in', query })
      return { persist: false, reason: 'not-opted-in' }
    }

    if (isInfiniteQueryData(query.state.data)) {
      reportPersistenceIssue({ reason: 'infinite', query })
      return { persist: false, reason: 'infinite' }
    }

    const data = query.state.data
    const cached = queryCloneDecisions.get(query)
    if (cached && Object.is(cached.data, data)) {
      return cached.decision
    }

    try {
      isStructuredCloneCompatible(data)
    } catch (error) {
      const reason = isDataCloneLikeError(error) ? 'not-cloneable' : 'error'
      reportPersistenceIssue({ reason, query, error })
      const decision: PersistDecision = { persist: false, reason }
      if (reason === 'not-cloneable') {
        queryCloneDecisions.set(query, { data, decision })
      }
      return decision
    }

    const decision = { persist: true }
    queryCloneDecisions.set(query, { data, decision })
    return decision
  } catch (error) {
    reportPersistenceIssue({ reason: 'error', query, error })
    return { persist: false, reason: 'error' }
  }
}

export const lookupOnlyDehydrateOptions = {
  shouldDehydrateMutation: () => false,
  shouldDehydrateQuery: (query) => shouldPersistLookupQuery(query),
}

export const buildQueryPersistKey = (scopeKey: string): string =>
  `${QUERY_PERSIST_PREFIX}${scopeKey}`

export const buildQueryPersistScopeKey = (
  sessionScopeKey: string,
  teamScopeKey: string,
): string => `${sessionScopeKey}:${teamScopeKey}`

export const createIDBPersister = (
  key: IDBValidKey = LEGACY_QUERY_PERSIST_KEY,
): Persister => {
  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let pendingClient: PersistedClient | undefined
  let pendingResolves: Array<() => void> = []

  const resolvePending = () => {
    const resolves = pendingResolves
    pendingResolves = []
    resolves.forEach((resolve) => resolve())
  }

  const flushPersistedClient = async () => {
    debounceTimer = null

    if (!pendingClient) {
      resolvePending()
      return
    }

    const clientToPersist = pendingClient
    pendingClient = undefined

    try {
      await set(key, clientToPersist)
    } catch (error) {
      const reason = isDataCloneLikeError(error) ? 'not-cloneable' : 'error'
      reportPersistenceIssue({ reason, error })
    } finally {
      resolvePending()
    }
  }

  const clearPendingWrite = () => {
    pendingClient = undefined

    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }

    resolvePending()
  }

  return {
    persistClient: async (client: PersistedClient) => {
      pendingClient = client

      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }

      await new Promise<void>((resolve) => {
        pendingResolves.push(resolve)
        debounceTimer = setTimeout(() => {
          void flushPersistedClient()
        }, QUERY_PERSIST_DEBOUNCE_MS)
      })
    },
    restoreClient: async () => {
      try {
        const persisted = await get<PersistedClient>(key)
        if (
          persisted &&
          persisted.clientState &&
          Array.isArray(persisted.clientState.queries)
        ) {
          return persisted
        }
        await del(key)
        return undefined
      } catch (error) {
        console.error('Error restoring cache:', error)
        await del(key)
        return undefined
      }
    },
    removeClient: async () => {
      clearPendingWrite()
      await del(key)
    },
  }
}

export async function clearPersistedQueryCache(
  scopeKey?: string,
): Promise<void> {
  try {
    if (scopeKey) {
      const normalizedKey = scopeKey.startsWith(QUERY_PERSIST_PREFIX)
        ? scopeKey
        : buildQueryPersistKey(scopeKey)

      await Promise.all([del(normalizedKey), del(scopeKey)])
      return
    }

    const allKeys = await keys()
    const toDelete = allKeys.filter(
      (value): value is IDBValidKey =>
        typeof value === 'string' &&
        (value.startsWith(QUERY_PERSIST_PREFIX) ||
          value === LEGACY_QUERY_PERSIST_KEY),
    )

    await Promise.all(toDelete.map((key) => del(key)))
  } catch (error) {
    console.error('Error clearing persisted query cache:', error)
  }
}
